'use client';

import type { AuthFormProps } from '../provider/types';

import type { LoginRequestData } from '@/apis/server/auth/v1/login/types';

import { use } from 'react';
import { useFormContext } from 'react-hook-form';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { LoginContext } from '../provider/context';
import { INPUT_FIELD_PADDING_Y } from './constant';
import { env } from '@/app/env';
import IcAppStore from '@/assets/icons/apps/app_store.svg';
import IcGooglePlay from '@/assets/icons/apps/google_play.svg';
import { CustomButton } from '@/components/common/custom-button';
import { FormControl as GeneralFormControl } from '@/components/common/form-control';
import { CustomInput } from '@/components/common/input';
import { InputPassword } from '@/components/common/input-password';
import { useRecaptchaSubmission } from '@/components/common/recaptcha/hooks';
import { checkIfRecaptchaRequired } from '@/components/common/recaptcha/utils';

import { usePageLoader } from '@/hooks/use-page-loader';
import { useResponseDialog } from '@/hooks/use-response-dialog';
import { cn } from '@/lib/utils';

const FormControl = GeneralFormControl<LoginRequestData>;

export const LoginFormComponent = () => {
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useFormContext<AuthFormProps>();
  const { setIsPageLoading } = usePageLoader();
  const isRecaptchaRequired = checkIfRecaptchaRequired();
  const { addDialog } = useResponseDialog();
  const router = useRouter();

  const { isSubmittingLogin, recaptchaRef, session, submitLogin }
    = use(LoginContext);

  const { onSubmitFormWithRecaptcha } = useRecaptchaSubmission({
    onErrorCallback: () => {
      addDialog({
        message:
          'Something went wrong while verifying reCAPTCHA. Please try again.',
        title: 'Error',
        type: 'error',
      });
    },
    onSuccessCallback: (token) => {
      setValue('recaptcha_token', token);
    },
    recaptchaRef,
  });

  const onSubmit = async (data: AuthFormProps) => {
    const isRecaptchaValid = await onSubmitFormWithRecaptcha();

    if (!isRecaptchaValid) {
      return;
    }

    setIsPageLoading(true);
    submitLogin(data);
  };

  const onError = () => {
    if (isRecaptchaRequired) {
      recaptchaRef.current?.reset();
    }
    addDialog({
      message:
        'Please ensure that all compulsory fields are filled out correctly.',
      title: 'Error',
      type: 'error',
    });
  };

  const onClickForgotPassword = () => {};

  const onManualSubmit = async () => {
    if (session) {
      router.push('/overview');
      return;
    }

    handleSubmit(onSubmit, onError)();
  };

  return (
    <main
      className={cn(
        'rounded-[0.6rem] bg-white',
        'w-full md:max-w-[46.5rem]',
        'mx-auto md:mt-24',
        'p-16',
      )}
    >
      <form
        className="flex flex-col gap-[1.4rem]"
        onSubmit={(e) => {
          e.preventDefault();
          onManualSubmit();
        }}
      >
        <Image
          alt="SGCM CMS"
          height={17.35}
          src={`${env.NEXT_PUBLIC_SGCM_IMAGE_MEDIA_URL}/components/header/sgcm-logo.svg`}
          width={120.7}
        />
        <h1 className="text-xl font-bold capitalize">
          Dealer Inventory Management System
        </h1>
        <Link
          className="text-sm text-link"
          href="/inventory/display/advertising_charges"
          target="_blank"
        >
          Current prevailing charges can be found here.
        </Link>
        <FormControl
          className="mb-0"
          errors={errors}
          inputName="email"
          isRequired
          showErrors
        >
          <CustomInput
            {...register('email')}
            autoComplete="username"
            className={INPUT_FIELD_PADDING_Y}
            isInvalid={Boolean(errors.email)}
            placeholder="Email Address*"
          />
        </FormControl>
        <FormControl
          className="mb-0"
          errors={errors}
          inputName="password"
          isRequired
          showErrors
        >
          <InputPassword
            inputGroupProps={{
              isInvalid: Boolean(errors.password),
            }}
            inputProps={{
              ...register('password'),
              className: INPUT_FIELD_PADDING_Y,
              isInvalid: Boolean(errors.password),
              placeholder: 'Password*',
            }}
          />
        </FormControl>
        <div className="flex justify-end">
          <Link
            className="text-sm font-medium text-link"
            href="/login/forget-password"
            onClick={onClickForgotPassword}
          >
            Forgot Password
          </Link>
        </div>
        <CustomButton
          className="w-full py-[0.9rem]"
          disabled={isSubmittingLogin}
          onClick={onManualSubmit}
          type="submit"
          variant="contained"
        >
          Login
        </CustomButton>
        <div className="flex justify-center gap-[1.4rem] pt-[0.8rem] md:hidden">
          <a
            href="https://itunes.apple.com/WebObjects/MZStore.woa/wa/viewSoftware?id=1202829921&mt=8"
            rel="noreferrer noopener"
            target="_blank"
          >
            <IcAppStore height={30} width={100} />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.inventoryDealer"
            rel="noreferrer noopener"
            target="_blank"
          >
            <IcGooglePlay height={30} width={100} />
          </a>
        </div>
      </form>
    </main>
  );
};
